import type {
  GetAssetUploadStatusQuery,
  GetAssetUploadStatusQueryVariables,
} from '../../../../../generated/graphql-manager';

import { useApolloClient } from '@apollo/client';
import { useCallback } from 'react';

import { GET_ASSET_UPLOAD_STATUS } from './speakers.api';
import {
  AssetUploadStatusCode,
  PictureType,
  useCreateSpeakerPictureMutation,
} from '../../../../../generated/graphql-manager';
import { uploadFile } from '../../../../../utils';

// The BE marks an uploaded asset as UPLOADED asynchronously, so we poll its status before linking
// it to a speaker (updateWebcast rejects a not-yet-uploaded picture).
const ASSET_STATUS_POLL_INTERVAL = 1500;
const ASSET_STATUS_POLL_TIMEOUT = 30000;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export type SpeakerImageUploader = {
  /**
   * Upload a single picked file at Save time and resolve its asset id: `createPicture` →
   * `uploadFile` → poll until UPLOADED. Rejects if the upload fails, is rejected, or times out.
   */
  uploadImage: (file: File) => Promise<string>;
};

export function useSpeakerImageUploader(webcastId: string): SpeakerImageUploader {
  const client = useApolloClient();
  const [createPictureMutation] = useCreateSpeakerPictureMutation();

  const uploadImage = useCallback(
    async (file: File): Promise<string> => {
      // 1. Reserve an asset and get its upload link.
      const created = await createPictureMutation({ variables: { input: { webcastId, type: PictureType.SPEAKER } } });
      const result = created.data?.createPicture;
      if (result?.__typename !== 'CreatePictureSuccess') throw new Error('createPicture failed');
      const { assetId, uploadLink } = result;

      // 2. Upload the file bytes.
      await new Promise<void>((resolve, reject) => {
        uploadFile({
          file,
          url: uploadLink.url,
          token: uploadLink.token,
          onSuccess: () => resolve(),
          onError: () => reject(new Error('upload failed')),
        });
      });

      // 3. Poll until the BE confirms the asset is UPLOADED.
      const maxAttempts = Math.ceil(ASSET_STATUS_POLL_TIMEOUT / ASSET_STATUS_POLL_INTERVAL);
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        let code: AssetUploadStatusCode | undefined;
        try {
          const { data } = await client.query<GetAssetUploadStatusQuery, GetAssetUploadStatusQueryVariables>({
            query: GET_ASSET_UPLOAD_STATUS,
            variables: { assetId },
            fetchPolicy: 'network-only',
          });
          code = data.assetUploadStatus?.code;
        } catch {
          // Transient error — keep polling until we time out.
        }

        if (code === AssetUploadStatusCode.UPLOADED) return assetId;
        if (code === AssetUploadStatusCode.FAILED) throw new Error('asset processing failed');

        await delay(ASSET_STATUS_POLL_INTERVAL);
      }

      throw new Error('asset upload timed out');
    },
    [client, createPictureMutation, webcastId]
  );

  return { uploadImage };
}
